/**
 * Lightbinder ability kit: graceful staff work in holy gold, white and soft rose-gold.
 *
 * The staff is held in the right fist, roughly at right angles to the forearm, gem up when the
 * forearm points forward (the base "staff hold": elbow bent ~1.25). Checked in the clip browser:
 *  - forearm forward (arm forward & straight, or hanging & elbow ~1.25) → staff upright;
 *  - forearm raised further (raise + elbow bend > ~2) → the gem tips back over the shoulder and
 *    the shaft end points forward: a spear jab led by the shaft end;
 *  - arm hanging, elbow straight      → shaft end forward-down (toward the floor);
 *  - arm overhead                     → staff lies flat over the head (helicopter twirls);
 *  - arm out sideways, straight       → staff hangs down from the hand (sweeps a wall of light);
 *  - hand roll (rightHand[0])         → spins the staff about the forearm: the twirl.
 * Trails and 'tip' cues come from the gem head (units.ts picks the +Y head end of the staff).
 *
 * Every strike starts and ends on the staff-hold pose (HOLD), so the staff never dips when a clip
 * takes the right arm over from the base gesture or hands it back.
 *
 * Full twirls: hand roll is a plain channel (only root spin is wrapped by the player), so a clip
 * that ends a twirl at 2π would unwind it while recovering. `settle()` adds a 'step' key right
 * after the twirl that jumps the roll (and root spin) back by whole turns, which looks identical.
 */
import { ABILITY } from '@keystone/content';
import { type Clip, type KeyDef, type PoseDef, defineClip } from '../clips';
import { registerAbilityClips } from '../registry';

const TAU = Math.PI * 2;

// --- palette (sRGB) ---------------------------------------------------------
const GOLD = '#ffd25e';
const GOLD_TAIL = '#a86a14';
const DEEP_GOLD = '#ffb627';
const DEEP_GOLD_TAIL = '#7a3d06';
const WHITE_GOLD = '#fff4cf';
const WHITE_GOLD_TAIL = '#d9a540';
const WHITE = '#f6fbff';
const WHITE_TAIL = '#8fd8f0';
const ROSE = '#ffb39b';
const ROSE_TAIL = '#b85a4c';

/** Base staff hold (units.ts): forearm forward so the staff stands upright, arm slightly in. */
const HOLD: PoseDef = { rightUpperArm: [0, 0, 0.1], rightLowerArm: [1.25] };

/** Merge `extra` into the HOLD pose. */
const held = (extra: PoseDef = {}): PoseDef => ({ ...HOLD, ...extra });

/**
 * Bracket `keys` with the staff-hold pose at t = 0 and at `duration` (recovery with `endEase`),
 * so strikes leave and return to the base staff gesture without the staff dipping.
 */
function strikeKeys(duration: number, keys: KeyDef[], endEase: KeyDef['ease'] = 'out'): KeyDef[] {
  return [{ t: 0, ease: 'linear', pose: HOLD }, ...keys, { t: duration, ease: endEase, pose: HOLD }];
}

/**
 * A key right after `k` (and its hold) with the staff roll and root spin reduced by whole turns,
 * reached with a 'step' ease: an invisible jump that keeps later segments from unwinding a twirl.
 */
function settle(k: KeyDef): KeyDef {
  const pose: PoseDef = { ...k.pose };
  for (const b of ['rightHand', 'leftHand'] as const) {
    const v = pose[b];
    if (v && v[0]) pose[b] = [v[0] - Math.round(v[0] / TAU) * TAU, v[1] ?? 0, v[2] ?? 0];
  }
  const root = k.root ? { ...k.root, spin: (k.root.spin ?? 0) - Math.round(k.root.spin ?? 0) } : undefined;
  return { t: k.t + (k.hold ?? 0) + 0.002, ease: 'step', pose, root };
}

// ===========================================================================
// Flash Heal (1.5 s cast): staff raised one-handed, tip circling a golden spiral; the release
// flings the staff forward through a full twirl.
// ===========================================================================

const FLASH_RAISE: PoseDef = {
  spine: [-0.06, 0.12, 0],
  chest: [-0.04, 0.05, 0],
  head: [-0.12, -0.08, 0],
  rightUpperArm: [1.95, 0.15, 0.3],
  rightLowerArm: [0.35],
  leftUpperArm: [1.05, 0.55, 0.25],
  leftLowerArm: [0.35],
  leftHand: [0, 0, -0.3],
  leftUpperLeg: [0.12, 0, 0.04],
  leftLowerLeg: [0.12],
  rightLowerLeg: [0.1],
};

/** One orbit of the staff tip: hand roll and arm sweep a quarter cycle apart. */
const flashOrbit = (roll: number, sweep: number, lift: number): PoseDef => ({
  ...FLASH_RAISE,
  rightUpperArm: [1.95 + lift, 0.15 + sweep, 0.3],
  rightHand: [roll, 0, 0],
});

export const flashHealCast = defineClip({
  id: 'lightbinder.flash_heal.cast',
  duration: 1.05,
  loop: true,
  loopFrom: 0.25,
  blendIn: 0.1,
  keys: [
    { t: 0, ease: 'linear', pose: HOLD },
    { t: 0.25, ease: 'out', pose: flashOrbit(0.75, 0, 0), root: { hop: 0.02 } },
    { t: 0.45, ease: 'inOut', pose: flashOrbit(0, 0.3, 0.08), root: { hop: 0.04 } },
    { t: 0.65, ease: 'inOut', pose: flashOrbit(-0.75, 0, 0.12), root: { hop: 0.03 } },
    { t: 0.85, ease: 'inOut', pose: flashOrbit(0, -0.25, 0.05), root: { hop: 0.01 } },
    // t = 1.05 closes back onto the loop-start pose.
  ],
  // One window spanning the loop: a continuous spiral ribbon while the cast bar runs.
  trails: [{ from: 0.2, to: 1.05, hand: 'right', color: GOLD, tail: GOLD_TAIL, style: 'holy', width: 0.45, fade: 0.42, alpha: 0.65 }],
  cues: [{ t: 0.25, fx: 'holy', at: 'tip', scale: 0.35 }],
});

const FLASH_TWIRL: KeyDef = {
  t: 0.27,
  ease: 'back',
  overshoot: 1.1,
  hold: 0.05,
  pose: {
    spine: [0.14, 0.25, 0],
    chest: [0.06, 0.1, 0],
    head: [-0.05, -0.2, 0],
    rightUpperArm: [1.35, 0.3, 0.1],
    rightLowerArm: [0.05],
    rightHand: [TAU, 0, 0],
    leftUpperArm: [1.25, 0.25, 0.45],
    leftLowerArm: [0.15],
    leftHand: [0, 0, -0.5],
    leftUpperLeg: [0.42, 0, 0.05],
    leftLowerLeg: [0.32],
    rightUpperLeg: [-0.18],
    rightLowerLeg: [0.22],
  },
  root: { lunge: 0.14, hop: -0.02 },
};

export const flashHeal = defineClip({
  id: 'lightbinder.flash_heal',
  duration: 0.6,
  blendIn: 0.07,
  keys: strikeKeys(0.6, [
    // Anticipation: staff drawn up and back, chest arched.
    {
      t: 0.09,
      ease: 'out',
      pose: { ...FLASH_RAISE, spine: [-0.14, 0.05, 0], head: [-0.2, 0, 0], rightUpperArm: [2.35, 0.05, 0.3], rightLowerArm: [0.3], rightHand: [-0.4, 0, 0] },
      root: { hop: 0.05, lunge: -0.05 },
    },
    // Release: the staff comes forward through one full twirl and stops upright, pointing at the ally.
    FLASH_TWIRL,
    settle(FLASH_TWIRL),
  ]),
  trails: [
    { from: 0.06, to: 0.34, hand: 'right', color: GOLD, tail: GOLD_TAIL, style: 'holy', width: 0.8, fade: 0.32, alpha: 0.85 },
    { from: 0.18, to: 0.32, hand: 'left', color: WHITE_GOLD, tail: WHITE_GOLD_TAIL, style: 'streak', width: 0.5, fade: 0.18, alpha: 0.5 },
  ],
  cues: [
    { t: 0.27, fx: 'holy', at: 'front', scale: 1.0 },
    { t: 0.27, fx: 'flash', at: 'tip', scale: 0.55, color: WHITE_GOLD },
  ],
});

// ===========================================================================
// Holy Shock (instant): a coiled, spear-like staff jab (shaft end leading) with a white-gold
// burst streak.
// ===========================================================================

const SHOCK_HIT: KeyDef = {
  t: 0.17,
  ease: 'snap',
  hold: 0.06,
  pose: {
    spine: [0.2, 0.38, 0],
    chest: [0.06, 0.12, 0],
    head: [-0.12, -0.4, 0],
    rightUpperArm: [1.1, 0.12, 0.2],
    rightLowerArm: [1.05],
    rightHand: [0, 0, 0],
    leftUpperArm: [0.15, -0.25, 0.65],
    leftLowerArm: [0.3],
    leftUpperLeg: [0.55, 0, 0.05],
    leftLowerLeg: [0.4],
    rightUpperLeg: [-0.28, 0, 0.05],
    rightLowerLeg: [0.25],
  },
  root: { lunge: 0.3, hop: -0.04 },
};

export const holyShock = defineClip({
  id: 'lightbinder.holy_shock',
  duration: 0.46,
  blendIn: 0.04,
  keys: strikeKeys(0.46, [
    // Coil: twist right, staff cocked back at the shoulder like a spear, off-hand guiding forward.
    {
      t: 0.09,
      ease: 'out',
      pose: {
        spine: [-0.04, -0.4, 0],
        chest: [0, -0.15, 0],
        head: [0, 0.4, 0],
        rightUpperArm: [0.3, -0.25, 0.6],
        rightLowerArm: [1.65],
        leftUpperArm: [0.75, 0.65, 0.2],
        leftLowerArm: [0.9],
        leftUpperLeg: [0.2, 0, 0.05],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.3],
      },
      root: { lunge: -0.08, hop: -0.03 },
    },
    SHOCK_HIT,
  ]),
  trails: [
    { from: 0.08, to: 0.25, hand: 'right', color: WHITE_GOLD, tail: DEEP_GOLD, style: 'streak', width: 0.55, fade: 0.15, alpha: 0.9 },
    { from: 0.12, to: 0.26, hand: 'right', color: GOLD, tail: GOLD_TAIL, style: 'holy', width: 0.3, fade: 0.2, alpha: 0.6 },
  ],
  cues: [
    { t: 0.17, fx: 'flash', at: 'tip', scale: 0.8, color: WHITE_GOLD },
    { t: 0.17, fx: 'holy', at: 'front', scale: 0.75 },
    { t: 0.18, fx: 'sparks', at: 'tip', scale: 0.55, color: GOLD },
  ],
  hitStop: { t: 0.17, ms: 35, shake: 0.08 },
});

// ===========================================================================
// Word of Light (spender): gather low, then rise onto the toes with both arms overhead and the
// staff spinning flat above the head; a bright pillar at the peak.
// ===========================================================================

const WORD_PEAK: KeyDef = {
  t: 0.34,
  ease: 'back',
  overshoot: 0.9,
  hold: 0.09,
  pose: {
    spine: [-0.2, 0, 0],
    chest: [-0.1, 0, 0],
    head: [-0.32, 0, 0],
    rightUpperArm: [2.75, 0.05, 0.25],
    rightLowerArm: [0.1],
    rightHand: [TAU * 1.5, 0, 0],
    leftUpperArm: [2.6, 0.05, 0.4],
    leftLowerArm: [0.15],
    leftHand: [0, 0, -0.4],
    leftFoot: [-0.35],
    rightFoot: [-0.35],
  },
  root: { hop: 0.12 },
};

export const wordOfLight = defineClip({
  id: 'lightbinder.word_of_light',
  duration: 0.68,
  blendIn: 0.05,
  keys: strikeKeys(0.68, [
    // Gather: crouch, both hands drawn in to the chest, head bowed.
    {
      t: 0.11,
      ease: 'out',
      pose: {
        spine: [0.3, 0, 0],
        chest: [0.08, 0, 0],
        head: [0.2, 0, 0],
        rightUpperArm: [0.35, 0.55, 0],
        rightLowerArm: [1.3],
        leftUpperArm: [0.35, 0.65, 0],
        leftLowerArm: [1.4],
        leftUpperLeg: [0.35, 0, 0.12],
        rightUpperLeg: [0.35, 0, 0.12],
        leftLowerLeg: [0.6],
        rightLowerLeg: [0.6],
        leftFoot: [0.15],
        rightFoot: [0.15],
      },
      root: { hop: -0.06 },
    },
    // Rise: arms sweep up overhead while the staff spins one and a half turns flat above the head.
    WORD_PEAK,
    settle(WORD_PEAK),
  ]),
  trails: [
    { from: 0.1, to: 0.46, hand: 'right', color: WHITE_GOLD, tail: WHITE_GOLD_TAIL, style: 'holy', width: 0.9, fade: 0.34, alpha: 0.85 },
    { from: 0.12, to: 0.34, hand: 'left', color: WHITE, tail: GOLD, style: 'streak', width: 0.5, fade: 0.2, alpha: 0.55 },
  ],
  cues: [
    { t: 0.34, fx: 'holy', at: 'self', scale: 1.6, color: WHITE_GOLD },
    { t: 0.34, fx: 'flash', at: 'tip', scale: 0.6, color: WHITE },
    { t: 0.36, fx: 'shockwave', at: 'ground', scale: 0.6, color: GOLD },
  ],
  hitStop: { t: 0.34, ms: 45, shake: 0.1 },
});

// ===========================================================================
// Judgment (instant damage): a hop up with the staff raised two-handed, then a hammer slam down
// in front. Deep-gold crescent arc, cracks and a shockwave.
// ===========================================================================

const JUDGE_SLAM: KeyDef = {
  t: 0.25,
  ease: 'snap',
  hold: 0.07,
  pose: {
    spine: [0.52, 0.12, 0],
    chest: [0.15, 0, 0],
    head: [-0.3, -0.1, 0],
    rightUpperArm: [0.95, 0.5, 0.05],
    rightLowerArm: [0.05],
    rightHand: [0, 0, 0.25],
    leftUpperArm: [1.0, 0.75, 0.05],
    leftLowerArm: [0.35],
    leftUpperLeg: [0.65, 0, 0.08],
    leftLowerLeg: [0.75],
    rightUpperLeg: [-0.3, 0, 0.08],
    rightLowerLeg: [0.6],
  },
  root: { lunge: 0.35, hop: -0.1 },
};

export const judgment = defineClip({
  id: 'lightbinder.judgment',
  duration: 0.58,
  blendIn: 0.04,
  keys: strikeKeys(0.58, [
    // Wind-up: rise onto the toes (a small hop), staff high and back over the shoulder, back arched.
    {
      t: 0.14,
      ease: 'out',
      pose: {
        spine: [-0.24, -0.22, 0],
        chest: [-0.08, -0.08, 0],
        head: [-0.1, 0.2, 0],
        rightUpperArm: [2.5, 0.15, 0.35],
        rightLowerArm: [0.55],
        leftUpperArm: [2.3, 0.5, 0.15],
        leftLowerArm: [0.8],
        leftUpperLeg: [0.4, 0, 0.05],
        leftLowerLeg: [0.6],
        rightUpperLeg: [-0.08],
        rightLowerLeg: [0.15],
      },
      root: { hop: 0.1, lunge: -0.05 },
    },
    JUDGE_SLAM,
  ]),
  trails: [
    { from: 0.12, to: 0.33, hand: 'right', color: DEEP_GOLD, tail: DEEP_GOLD_TAIL, style: 'crescent', width: 0.95, fade: 0.26, alpha: 0.9 },
    { from: 0.15, to: 0.3, hand: 'right', color: WHITE_GOLD, tail: GOLD, style: 'holy', width: 0.4, fade: 0.2, alpha: 0.6 },
  ],
  cues: [
    { t: 0.25, fx: 'slash', at: 'tip', scale: 1.15 },
    { t: 0.25, fx: 'impact', at: 'ground', scale: 0.8, color: GOLD },
    { t: 0.26, fx: 'shockwave', at: 'ground', scale: 0.8, color: GOLD },
    { t: 0.26, fx: 'cracks', at: 'ground', scale: 0.6 },
  ],
  hitStop: { t: 0.25, ms: 60, shake: 0.2 },
});

// ===========================================================================
// Radiant Wave (AoE heal): coil low, then a full clockwise spin with the staff swept out level,
// finishing planted; a golden ring trail and an expanding shockwave.
// ===========================================================================

const WAVE_ARMS_OUT: PoseDef = {
  spine: [0.12, 0.1, 0],
  head: [-0.05, 0, 0],
  rightUpperArm: [0.35, -0.05, 1.2],
  rightLowerArm: [0.08],
  leftUpperArm: [0.35, 0, 1.15],
  leftLowerArm: [0.15],
  leftUpperLeg: [0.25, 0, 0.2],
  rightUpperLeg: [0.25, 0, 0.2],
  leftLowerLeg: [0.45],
  rightLowerLeg: [0.45],
};

const WAVE_PLANT: KeyDef = {
  t: 0.5,
  ease: 'out',
  hold: 0.07,
  pose: {
    spine: [0.32, 0.05, 0],
    chest: [0.08, 0, 0],
    head: [-0.2, 0, 0],
    rightUpperArm: [0.55, 0.35, 0.2],
    rightLowerArm: [1.1],
    leftUpperArm: [0.4, -0.2, 0.85],
    leftLowerArm: [0.2],
    leftUpperLeg: [0.55, 0, 0.15],
    rightUpperLeg: [-0.15, 0, 0.15],
    leftLowerLeg: [0.75],
    rightLowerLeg: [0.6],
  },
  root: { spin: -1, hop: -0.08 },
};

export const radiantWave = defineClip({
  id: 'lightbinder.radiant_wave',
  duration: 0.82,
  blendIn: 0.05,
  keys: strikeKeys(0.82, [
    // Coil: twist right, crouch, staff drawn across low on the right.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [0.15, -0.5, 0],
        chest: [0.05, -0.15, 0],
        head: [0, 0.4, 0],
        rightUpperArm: [0.2, -0.35, 0.8],
        rightLowerArm: [0.4],
        leftUpperArm: [0.6, 0.75, 0.3],
        leftLowerArm: [0.9],
        leftUpperLeg: [0.35, 0, 0.12],
        rightUpperLeg: [0.2, 0, 0.12],
        leftLowerLeg: [0.6],
        rightLowerLeg: [0.55],
      },
      root: { hop: -0.06, spin: 0.06 },
    },
    // The sweep: arms flung out, staff level, a small lift as the body turns.
    { t: 0.3, ease: 'in', pose: WAVE_ARMS_OUT, root: { spin: -0.5, hop: 0.08 } },
    { t: 0.42, ease: 'linear', pose: { ...WAVE_ARMS_OUT, spine: [0.15, 0.2, 0.08] }, root: { spin: -0.92, hop: 0.04 } },
    // Plant: staff driven down at the end of the turn.
    WAVE_PLANT,
    settle(WAVE_PLANT),
  ]),
  trails: [
    { from: 0.14, to: 0.52, hand: 'right', color: GOLD, tail: GOLD_TAIL, style: 'holy', width: 0.95, fade: 0.36, alpha: 0.85 },
    { from: 0.16, to: 0.46, hand: 'right', color: WHITE_GOLD, tail: DEEP_GOLD, style: 'crescent', width: 0.35, fade: 0.24, alpha: 0.55 },
    { from: 0.2, to: 0.46, hand: 'left', color: ROSE, tail: ROSE_TAIL, style: 'streak', width: 0.5, fade: 0.2, alpha: 0.5 },
  ],
  cues: [
    { t: 0.5, fx: 'shockwave', at: 'ground', scale: 1.6 },
    { t: 0.5, fx: 'holy', at: 'self', scale: 1.1 },
    { t: 0.52, fx: 'dust', at: 'ground', scale: 0.6 },
  ],
  hitStop: { t: 0.5, ms: 40, shake: 0.15 },
});

// ===========================================================================
// Guardian's Aegis (off-GCD external): staff lifted and planted with a thud, then the open
// left hand sweeps a protective arc out toward the ally.
// ===========================================================================

export const guardiansAegis = defineClip({
  id: 'lightbinder.guardians_aegis',
  duration: 0.6,
  blendIn: 0.04,
  keys: strikeKeys(0.6, [
    // Lift: staff raised upright, off-hand drawn across the chest.
    {
      t: 0.09,
      ease: 'out',
      pose: {
        spine: [-0.06, -0.15, 0],
        rightUpperArm: [0.95, 0.2, 0.15],
        rightLowerArm: [1.0],
        leftUpperArm: [0.9, 1.0, 0.05],
        leftLowerArm: [1.5],
        leftUpperLeg: [0.15],
        leftLowerLeg: [0.15],
      },
      root: { hop: 0.06 },
    },
    // Plant: staff slammed down upright, knees give.
    {
      t: 0.18,
      ease: 'snap',
      hold: 0.06,
      pose: {
        spine: [0.18, -0.1, 0],
        head: [0.05, 0.1, 0],
        rightUpperArm: [0.3, 0.25, 0.2],
        rightLowerArm: [1.4],
        leftUpperArm: [0.95, 1.0, 0.05],
        leftLowerArm: [1.45],
        leftUpperLeg: [0.35, 0, 0.1],
        rightUpperLeg: [0.15, 0, 0.1],
        leftLowerLeg: [0.5],
        rightLowerLeg: [0.45],
      },
      root: { hop: -0.07 },
    },
    // Sweep: the open hand opens out wide toward the ally, torso turning with it.
    {
      t: 0.38,
      ease: 'back',
      overshoot: 1.0,
      pose: {
        spine: [0.08, 0.3, 0],
        chest: [0, 0.12, 0],
        head: [-0.05, -0.25, 0],
        rightUpperArm: [0.3, 0.25, 0.2],
        rightLowerArm: [1.4],
        leftUpperArm: [1.25, -0.45, 0.75],
        leftLowerArm: [0.1],
        leftHand: [0, 0, -0.5],
        leftUpperLeg: [0.3, 0, 0.1],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.25],
      },
      root: { lunge: 0.08, hop: -0.02 },
    },
  ]),
  trails: [
    { from: 0.05, to: 0.22, hand: 'right', color: GOLD, tail: GOLD_TAIL, style: 'holy', width: 0.6, fade: 0.25, alpha: 0.7 },
    { from: 0.22, to: 0.44, hand: 'left', color: '#ffe08a', tail: WHITE_GOLD_TAIL, style: 'holy', width: 0.9, fade: 0.36, alpha: 0.85 },
  ],
  cues: [
    { t: 0.18, fx: 'shockwave', at: 'ground', scale: 0.85, color: GOLD },
    { t: 0.18, fx: 'dust', at: 'ground', scale: 0.5 },
    { t: 0.37, fx: 'holy', at: 'front', scale: 0.9, color: '#ffe08a' },
  ],
  hitStop: { t: 0.18, ms: 30, shake: 0.12 },
});

// ===========================================================================
// Cleanse: a quick flick of the off-hand wrist that throws white sparkles; the staff answers
// with a small half-flourish.
// ===========================================================================

export const cleanse = defineClip({
  id: 'lightbinder.cleanse',
  duration: 0.4,
  blendIn: 0.04,
  keys: strikeKeys(0.4, [
    // Cock: hand curled in at the shoulder, wrist bent back.
    {
      t: 0.08,
      ease: 'out',
      pose: {
        spine: [0, -0.12, 0],
        head: [0, 0.15, 0],
        rightUpperArm: [0, 0, 0.1],
        rightLowerArm: [1.25],
        rightHand: [-0.25, 0, 0],
        leftUpperArm: [0.7, 0.85, 0.15],
        leftLowerArm: [1.7],
        leftHand: [0, 0, 0.7],
      },
    },
    // Flick: the hand snaps open forward-out, the staff tips back the other way.
    {
      t: 0.15,
      ease: 'snap',
      hold: 0.04,
      pose: {
        spine: [0.06, 0.18, 0],
        head: [-0.05, -0.2, 0],
        rightUpperArm: [0.05, 0, 0.12],
        rightLowerArm: [1.25],
        rightHand: [0.2, 0, 0],
        leftUpperArm: [1.35, -0.1, 0.45],
        leftLowerArm: [0.05],
        leftHand: [0, 0, -0.6],
        leftUpperLeg: [0.15],
        leftLowerLeg: [0.12],
      },
      root: { lunge: 0.05 },
    },
  ]),
  trails: [
    { from: 0.07, to: 0.22, hand: 'left', color: WHITE, tail: WHITE_TAIL, style: 'holy', width: 0.7, fade: 0.26, alpha: 0.85 },
    { from: 0.06, to: 0.2, hand: 'right', color: WHITE, tail: WHITE_TAIL, style: 'streak', width: 0.3, fade: 0.14, alpha: 0.4 },
  ],
  cues: [
    { t: 0.15, fx: 'sparks', at: 'hand', hand: 'left', scale: 0.7, color: WHITE },
    { t: 0.15, fx: 'flash', at: 'hand', hand: 'left', scale: 0.4, color: WHITE },
    { t: 0.17, fx: 'holy', at: 'front', scale: 0.5, color: WHITE },
  ],
});

// ===========================================================================
// Divine Shield (self, off-GCD): drop to one knee, the staff planted upright in front, head
// bowed; a white-gold flare.
// ===========================================================================

export const divineShield = defineClip({
  id: 'lightbinder.divine_shield',
  duration: 0.9,
  blendIn: 0.04,
  keys: strikeKeys(0.9, [
    // Lift the staff, rise a little.
    {
      t: 0.1,
      ease: 'out',
      pose: { spine: [-0.08, 0, 0], head: [-0.1, 0, 0], rightUpperArm: [0.9, 0.25, 0.1], rightLowerArm: [0.9], leftUpperArm: [0.4, 0.6, 0.1], leftLowerArm: [1.1] },
      root: { hop: 0.05 },
    },
    // Kneel: right knee down, left foot forward, staff planted upright, off-hand on the staff, head bowed.
    {
      t: 0.26,
      ease: 'snap',
      hold: 0.22,
      pose: {
        spine: [0.22, 0.05, 0],
        chest: [0.05, 0, 0],
        head: [0.4, 0, 0],
        rightUpperArm: [0.85, 0.35, 0],
        rightLowerArm: [0.75],
        leftUpperArm: [0.95, 0.95, 0],
        leftLowerArm: [1.0],
        leftUpperLeg: [1.35, 0, 0.12],
        leftLowerLeg: [1.35],
        leftFoot: [-0.1],
        rightUpperLeg: [-0.15, 0, 0.1],
        rightLowerLeg: [1.55],
        rightFoot: [0.45],
      },
      root: { hop: -0.42, lunge: -0.05 },
    },
  ]),
  trails: [{ from: 0.06, to: 0.3, hand: 'right', color: WHITE_GOLD, tail: WHITE_GOLD_TAIL, style: 'holy', width: 0.7, fade: 0.3, alpha: 0.75 }],
  cues: [
    { t: 0.26, fx: 'holy', at: 'self', scale: 1.4, color: WHITE_GOLD },
    { t: 0.26, fx: 'flash', at: 'self', scale: 0.7, color: WHITE },
    { t: 0.27, fx: 'shockwave', at: 'ground', scale: 0.8, color: WHITE_GOLD },
  ],
  hitStop: { t: 0.26, ms: 30, shake: 0.06 },
});

// ===========================================================================
// Blessing of Freedom (off-GCD external): arms crossed in front, then flung outward and up,
// trailing rose-gold ribbons, with a half-turn of the staff.
// ===========================================================================

export const blessingOfFreedom = defineClip({
  id: 'lightbinder.blessing_of_freedom',
  duration: 0.55,
  blendIn: 0.04,
  keys: strikeKeys(0.55, [
    // Gather: arms crossed in front, slight crouch.
    {
      t: 0.1,
      ease: 'out',
      pose: {
        spine: [0.18, 0, 0],
        head: [0.12, 0, 0],
        rightUpperArm: [0.9, 0.85, 0],
        rightLowerArm: [0.7],
        leftUpperArm: [0.9, 0.85, 0],
        leftLowerArm: [0.7],
        leftUpperLeg: [0.2, 0, 0.08],
        rightUpperLeg: [0.2, 0, 0.08],
        leftLowerLeg: [0.35],
        rightLowerLeg: [0.35],
      },
      root: { hop: -0.04 },
    },
    // Release: arms flung wide and up, chest open, up onto the toes.
    {
      t: 0.23,
      ease: 'back',
      overshoot: 1.3,
      hold: 0.04,
      pose: {
        spine: [-0.14, 0, 0],
        chest: [-0.08, 0, 0],
        head: [-0.22, 0, 0],
        rightUpperArm: [1.55, -0.45, 1.0],
        rightLowerArm: [0.1],
        rightHand: [-0.9, 0, 0],
        leftUpperArm: [1.55, -0.45, 1.0],
        leftLowerArm: [0.1],
        leftHand: [0, 0, -0.5],
        leftFoot: [-0.25],
        rightFoot: [-0.25],
      },
      root: { hop: 0.08 },
    },
  ]),
  trails: [
    { from: 0.08, to: 0.31, hand: 'both', color: ROSE, tail: ROSE_TAIL, style: 'holy', width: 0.85, fade: 0.34, alpha: 0.85 },
    { from: 0.12, to: 0.29, hand: 'right', color: WHITE_GOLD, tail: GOLD, style: 'streak', width: 0.35, fade: 0.18, alpha: 0.5 },
  ],
  cues: [
    { t: 0.23, fx: 'holy', at: 'self', scale: 0.9, color: ROSE },
    { t: 0.23, fx: 'sparks', at: 'hand', hand: 'left', scale: 0.5, color: ROSE },
    { t: 0.24, fx: 'sparks', at: 'tip', scale: 0.5, color: WHITE_GOLD },
  ],
});

// ===========================================================================
// Rotation rework: Dawnsong (5), Dawnbringer (6) and its transform Sunfall. Dawn palette: warm
// sunrise gold, peach and a hot orange for Sunfall (the sun brought down).
// ===========================================================================

const DAWN = '#ffd98a';
const DAWN_TAIL = '#c8743a';
const PEACH = '#ffc49a';
const PEACH_TAIL = '#a8504a';
const SUN = '#ffb347';
const SUN_TAIL = '#a8380e';
const SUNCORE = '#fff0c4';

// ---------------------------------------------------------------------------
// Dawnsong (spender, group heal): the sunrise arc. The open left hand rises from the right hip in
// a wide arc across the body to high out on the left, the staff swings up with one twirl on the
// other side, the head lifts to follow the rising hand. Asymmetric and airy (Word of Light is the
// symmetric both-arms-up pillar).
// ---------------------------------------------------------------------------

const SONG_PEAK: KeyDef = {
  t: 0.36,
  ease: 'back',
  overshoot: 1.0,
  hold: 0.12,
  pose: {
    spine: [-0.16, 0.18, -0.1],
    chest: [-0.08, 0.08, 0],
    head: [-0.35, 0.25, 0],
    rightUpperArm: [1.55, -0.35, 0.75],
    rightLowerArm: [0.45],
    rightHand: [TAU, 0, 0],
    leftUpperArm: [2.2, -0.65, 1.0],
    leftLowerArm: [0.05],
    leftHand: [0, 0, -0.6],
    leftUpperLeg: [-0.12, 0, 0.06],
    rightUpperLeg: [0.25, 0, 0.06],
    rightLowerLeg: [0.35],
    leftFoot: [-0.3],
  },
  root: { hop: 0.1, side: -0.06 },
};

export const dawnsong = defineClip({
  id: 'lightbinder.dawnsong',
  duration: 0.78,
  blendIn: 0.05,
  keys: strikeKeys(0.78, [
    // Gather: dip and turn right, the open hand low by the right hip, staff lowered.
    {
      t: 0.12,
      ease: 'out',
      pose: {
        spine: [0.22, -0.35, 0.05],
        chest: [0.06, -0.12, 0],
        head: [0.12, 0.3, 0],
        rightUpperArm: [0.15, -0.2, 0.35],
        rightLowerArm: [1.0],
        rightHand: [-0.35, 0, 0],
        leftUpperArm: [0.35, 0.95, 0.05],
        leftLowerArm: [0.6],
        leftHand: [0, 0, 0.4],
        leftUpperLeg: [0.3, 0, 0.1],
        rightUpperLeg: [0.3, 0, 0.1],
        leftLowerLeg: [0.5],
        rightLowerLeg: [0.5],
      },
      root: { hop: -0.06, side: 0.04 },
    },
    // Rise: the hand sweeps its arc up and out, the staff twirls once, up onto the toes.
    SONG_PEAK,
    settle(SONG_PEAK),
  ]),
  trails: [
    { from: 0.1, to: 0.5, hand: 'left', color: DAWN, tail: DAWN_TAIL, style: 'holy', width: 1, fade: 0.42, alpha: 0.85 },
    { from: 0.12, to: 0.46, hand: 'right', color: WHITE_GOLD, tail: WHITE_GOLD_TAIL, style: 'holy', width: 0.7, fade: 0.32, alpha: 0.75 },
    { from: 0.16, to: 0.4, hand: 'left', color: PEACH, tail: PEACH_TAIL, style: 'streak', width: 0.45, fade: 0.2, alpha: 0.5 },
  ],
  cues: [
    { t: 0.36, fx: 'holy', at: 'self', scale: 1.5, color: DAWN },
    { t: 0.36, fx: 'sparks', at: 'hand', hand: 'left', scale: 0.7, color: WHITE_GOLD },
    { t: 0.37, fx: 'flash', at: 'tip', scale: 0.55, color: SUNCORE },
    { t: 0.4, fx: 'shockwave', at: 'ground', scale: 0.9, color: PEACH },
  ],
  hitStop: { t: 0.36, ms: 30, shake: 0.06 },
});

// ---------------------------------------------------------------------------
// Dawnbringer (60 s cooldown): the sun rises. A deep kneel with the staff laid across both
// palms, then a spiralling rise: one full counter-clockwise body turn while the staff is
// hoisted two-handed overhead (the gem traces a halo), ending on the toes with the light held up.
// ---------------------------------------------------------------------------

const DAWN_RAISED: KeyDef = {
  t: 0.42,
  ease: 'out',
  hold: 0.14,
  pose: {
    spine: [-0.24, 0, 0],
    chest: [-0.12, 0, 0],
    head: [-0.4, 0, 0],
    rightUpperArm: [2.7, 0.15, 0.4],
    rightLowerArm: [0.35],
    leftUpperArm: [2.7, 0.15, 0.4],
    leftLowerArm: [0.35],
    leftHand: [0, 0, -0.3],
    leftFoot: [-0.4],
    rightFoot: [-0.4],
  },
  root: { hop: 0.2, spin: 1 },
};

export const dawnbringer = defineClip({
  id: 'lightbinder.dawnbringer',
  duration: 0.9,
  blendIn: 0.05,
  keys: strikeKeys(0.9, [
    // Kneel: down on the right knee, head bowed, staff laid flat across both forearms.
    {
      t: 0.14,
      ease: 'out',
      pose: {
        spine: [0.4, 0, 0],
        chest: [0.1, 0, 0],
        head: [0.3, 0, 0],
        rightUpperArm: [0.7, 0.55, 0.05],
        rightLowerArm: [1.1],
        leftUpperArm: [0.7, 0.6, 0.05],
        leftLowerArm: [1.2],
        leftUpperLeg: [1.0, 0, 0.1],
        leftLowerLeg: [1.1],
        rightUpperLeg: [0.1, 0, 0.1],
        rightLowerLeg: [1.3],
        rightFoot: [0.35],
      },
      root: { hop: -0.3 },
    },
    // Spiral up: halfway round, arms climbing.
    {
      t: 0.3,
      ease: 'in',
      pose: {
        spine: [0.05, 0, 0.08],
        head: [-0.1, 0, 0],
        rightUpperArm: [1.9, 0.2, 0.45],
        rightLowerArm: [0.5],
        leftUpperArm: [1.9, 0.2, 0.45],
        leftLowerArm: [0.5],
        leftUpperLeg: [0.25],
        leftLowerLeg: [0.3],
        rightLowerLeg: [0.25],
      },
      root: { hop: 0.05, spin: 0.55 },
    },
    // Raised: the turn completes, staff held high, the sunburst.
    DAWN_RAISED,
    settle(DAWN_RAISED),
  ]),
  trails: [
    { from: 0.16, to: 0.6, hand: 'right', color: DAWN, tail: DAWN_TAIL, style: 'holy', width: 1, fade: 0.45, alpha: 0.9 },
    { from: 0.18, to: 0.5, hand: 'right', color: SUNCORE, tail: DEEP_GOLD, style: 'crescent', width: 0.4, fade: 0.28, alpha: 0.6 },
    { from: 0.2, to: 0.52, hand: 'left', color: PEACH, tail: PEACH_TAIL, style: 'holy', width: 0.8, fade: 0.36, alpha: 0.6 },
  ],
  cues: [
    { t: 0.14, fx: 'holy', at: 'ground', scale: 0.7, color: DAWN },
    { t: 0.42, fx: 'flash', at: 'tip', scale: 1.0, color: SUNCORE },
    { t: 0.42, fx: 'holy', at: 'self', scale: 2.0, color: DAWN },
    { t: 0.43, fx: 'shockwave', at: 'ground', scale: 1.8, color: DEEP_GOLD },
    { t: 0.46, fx: 'sparks', at: 'tip', scale: 0.9, color: WHITE_GOLD },
  ],
  hitStop: { t: 0.42, ms: 55, shake: 0.18 },
});

// ---------------------------------------------------------------------------
// Sunfall (Dawnbringer's transform): call the sun down. The staff is thrust straight up into the
// sky with a small hop (the sun gathers at the gem), then hauled down in a great forward arc with
// one forward twirl, the caster dropping into a deep lunge with the gem pointed at the target.
// Hot sunset orange over gold.
// ---------------------------------------------------------------------------

const SUNFALL_HIT: KeyDef = {
  t: 0.38,
  ease: 'snap',
  hold: 0.1,
  pose: {
    spine: [0.5, 0.2, 0],
    chest: [0.14, 0.05, 0],
    head: [-0.35, -0.15, 0],
    rightUpperArm: [1.25, 0.3, 0.15],
    rightLowerArm: [0.05],
    rightHand: [TAU, 0, 0.2],
    leftUpperArm: [0.2, -0.3, 0.85],
    leftLowerArm: [0.3],
    leftHand: [0, 0, -0.4],
    leftUpperLeg: [0.75, 0, 0.1],
    leftLowerLeg: [0.8],
    rightUpperLeg: [-0.35, 0, 0.1],
    rightLowerLeg: [0.5],
  },
  root: { lunge: 0.4, hop: -0.14 },
};

export const sunfall = defineClip({
  id: 'lightbinder.sunfall',
  duration: 0.82,
  blendIn: 0.04,
  keys: strikeKeys(0.82, [
    // Call: staff thrust straight up one-handed, the off-hand reaching up beside it, a hop.
    {
      t: 0.16,
      ease: 'out',
      pose: {
        spine: [-0.26, -0.1, 0],
        chest: [-0.12, -0.05, 0],
        head: [-0.45, 0, 0],
        rightUpperArm: [2.85, 0.05, 0.2],
        rightLowerArm: [0.1],
        leftUpperArm: [2.3, 0.2, 0.55],
        leftLowerArm: [0.25],
        leftFoot: [-0.35],
        rightFoot: [-0.35],
      },
      root: { hop: 0.16, lunge: -0.06 },
    },
    // Brief hang at the top while the light gathers.
    {
      t: 0.24,
      ease: 'linear',
      pose: {
        spine: [-0.28, -0.12, 0],
        chest: [-0.13, -0.05, 0],
        head: [-0.45, 0, 0],
        rightUpperArm: [2.9, 0.05, 0.22],
        rightLowerArm: [0.12],
        leftUpperArm: [2.35, 0.2, 0.58],
        leftLowerArm: [0.25],
        leftFoot: [-0.35],
        rightFoot: [-0.35],
      },
      root: { hop: 0.18, lunge: -0.06 },
    },
    // The fall: one great forward arc into a deep lunge, the gem levelled at the target.
    SUNFALL_HIT,
    settle(SUNFALL_HIT),
  ]),
  trails: [
    { from: 0.22, to: 0.46, hand: 'right', color: SUN, tail: SUN_TAIL, style: 'crescent', width: 1, fade: 0.3, alpha: 0.9 },
    { from: 0.12, to: 0.48, hand: 'right', color: SUNCORE, tail: DEEP_GOLD, style: 'holy', width: 0.7, fade: 0.36, alpha: 0.7 },
    { from: 0.14, to: 0.4, hand: 'left', color: DAWN, tail: DAWN_TAIL, style: 'streak', width: 0.5, fade: 0.2, alpha: 0.5 },
  ],
  cues: [
    { t: 0.22, fx: 'flash', at: 'tip', scale: 0.8, color: SUNCORE },
    { t: 0.38, fx: 'impact', at: 'front', scale: 1.1, color: SUN },
    { t: 0.38, fx: 'holy', at: 'front', scale: 1.4, color: DAWN },
    { t: 0.39, fx: 'shockwave', at: 'ground', scale: 1.3, color: SUN },
    { t: 0.4, fx: 'embers', at: 'front', scale: 1.0, color: SUN },
  ],
  hitStop: { t: 0.38, ms: 65, shake: 0.22 },
});

/** Every lightbinder ability → its clips (the test checks this covers the whole class). */
export const LIGHTBINDER_CLIPS: Readonly<Record<string, { strike?: Clip; cast?: Clip; channel?: Clip }>> = {
  [ABILITY.lightbinder.flashHeal]: { strike: flashHeal, cast: flashHealCast },
  [ABILITY.lightbinder.holyShock]: { strike: holyShock },
  [ABILITY.lightbinder.wordOfLight]: { strike: wordOfLight },
  [ABILITY.lightbinder.judgment]: { strike: judgment },
  [ABILITY.lightbinder.radiantWave]: { strike: radiantWave },
  [ABILITY.lightbinder.guardiansAegis]: { strike: guardiansAegis },
  [ABILITY.lightbinder.cleanse]: { strike: cleanse },
  [ABILITY.lightbinder.divineShield]: { strike: divineShield },
  [ABILITY.lightbinder.blessingOfFreedom]: { strike: blessingOfFreedom },
  [ABILITY.lightbinder.dawnsong]: { strike: dawnsong },
  [ABILITY.lightbinder.dawnbringer]: { strike: dawnbringer },
  [ABILITY.lightbinder.sunfall]: { strike: sunfall },
};

export function registerLightbinderKit(): void {
  for (const [id, clips] of Object.entries(LIGHTBINDER_CLIPS)) registerAbilityClips(id, clips);
}
